import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import MainLayout from '../../components/layout/MainLayout';
import billingApi, { SubscriptionPlan } from '../../services/api/billing.api';
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';

export default function PlansPage() {
  const { t } = useTranslation();
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<SubscriptionPlan | null>(null);
  const [deletingPlan, setDeletingPlan] = useState<SubscriptionPlan | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    price_monthly: 0,
    price_annual: 0,
    // BRL pricing
    price_monthly_brl: 0,
    price_annual_brl: 0,
    currency: 'USD',
    max_applications: 1,
    max_users: 1,
    max_hosts: 1,
    data_retention_days: 7,
    // Pay-as-you-go pricing (USD)
    price_per_infra_host: 0,
    price_per_apm_host: 0,
    price_per_kubernetes_node: 0,
    price_per_gb_log_ingestion: 0,
    price_per_million_indexed_spans: 0,
    price_per_100_custom_metrics: 0,
    // Pay-as-you-go pricing (BRL)
    price_per_infra_host_brl: 0,
    price_per_apm_host_brl: 0,
    price_per_kubernetes_node_brl: 0,
    price_per_gb_log_ingestion_brl: 0,
    price_per_million_indexed_spans_brl: 0,
    price_per_100_custom_metrics_brl: 0,
    // Included allotments
    included_infra_hosts: 0,
    included_apm_hosts: 0,
    included_kubernetes_nodes: 0,
    included_gb_log_ingestion: 0,
    included_million_indexed_spans: 0,
    included_custom_metrics: 0,
    // RUM & Session Recordings pricing (USD)
    price_per_1000_rum_sessions: 0,
    price_per_million_rum_events: 0,
    price_per_1000_recordings: 0,
    price_per_gb_recording_storage: 0,
    // RUM & Session Recordings pricing (BRL)
    price_per_1000_rum_sessions_brl: 0,
    price_per_million_rum_events_brl: 0,
    price_per_1000_recordings_brl: 0,
    price_per_gb_recording_storage_brl: 0,
    // RUM & Session Recordings included allotments
    included_rum_sessions: 0,
    included_rum_events: 0,
    included_session_recordings: 0,
    included_recording_storage_gb: 0,
    // Cloud Scans
    included_cloud_scans: 0,
    price_per_cloud_scan: 9.99,
    price_per_cloud_scan_brl: 49.99,
    // Uptime Monitors
    included_uptime_monitors: 0,
    price_per_uptime_monitor: 4.99,
    price_per_uptime_monitor_brl: 24.99,
    // Security Scans
    included_security_scans: 0,
    price_per_security_scan: 14.99,
    price_per_security_scan_brl: 74.99,
    // SEO Scans
    included_seo_scans: 0,
    price_per_seo_scan: 4.99,
    price_per_seo_scan_brl: 24.99,
    // Legacy fields (mantém para compatibilidade)
    included_metrics_events: 100000,
    included_log_events: 500000,
    included_trace_spans: 100000,
    included_storage_gb: 1,
    overage_metrics_per_1k: 0,
    overage_logs_per_1k: 0,
    overage_traces_per_1k: 0,
    overage_storage_per_gb: 0,
    features: [] as string[],
    is_public: true,
    is_active: true,
    sort_order: 0,
  });

  const [featureInput, setFeatureInput] = useState('');

  useEffect(() => {
    loadPlans();
  }, [showInactive]);

  const loadPlans = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await billingApi.getAllPlans(showInactive);
      if (response.success) {
        setPlans(response.data.sort((a, b) => a.sort_order - b.sort_order));
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load plans');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      slug: '',
      description: '',
      price_monthly: 0,
      price_annual: 0,
      price_monthly_brl: 0,
      price_annual_brl: 0,
      currency: 'USD',
      max_applications: 1,
      max_users: 1,
      max_hosts: 1,
      data_retention_days: 7,
      // Pay-as-you-go pricing (USD)
      price_per_infra_host: 0,
      price_per_apm_host: 0,
      price_per_kubernetes_node: 0,
      price_per_gb_log_ingestion: 0,
      price_per_million_indexed_spans: 0,
      price_per_100_custom_metrics: 0,
      // Pay-as-you-go pricing (BRL)
      price_per_infra_host_brl: 0,
      price_per_apm_host_brl: 0,
      price_per_kubernetes_node_brl: 0,
      price_per_gb_log_ingestion_brl: 0,
      price_per_million_indexed_spans_brl: 0,
      price_per_100_custom_metrics_brl: 0,
      // Included allotments
      included_infra_hosts: 0,
      included_apm_hosts: 0,
      included_kubernetes_nodes: 0,
      included_gb_log_ingestion: 0,
      included_million_indexed_spans: 0,
      included_custom_metrics: 0,
      // RUM & Session Recordings pricing (USD)
      price_per_1000_rum_sessions: 0,
      price_per_million_rum_events: 0,
      price_per_1000_recordings: 0,
      price_per_gb_recording_storage: 0,
      // RUM & Session Recordings pricing (BRL)
      price_per_1000_rum_sessions_brl: 0,
      price_per_million_rum_events_brl: 0,
      price_per_1000_recordings_brl: 0,
      price_per_gb_recording_storage_brl: 0,
      // RUM & Session Recordings included allotments
      included_rum_sessions: 0,
      included_rum_events: 0,
      included_session_recordings: 0,
      included_recording_storage_gb: 0,
      // Cloud Scans
      included_cloud_scans: 0,
      price_per_cloud_scan: 9.99,
      price_per_cloud_scan_brl: 49.99,
      // Uptime Monitors
      included_uptime_monitors: 0,
      price_per_uptime_monitor: 4.99,
      price_per_uptime_monitor_brl: 24.99,
      // Security Scans
      included_security_scans: 0,
      price_per_security_scan: 14.99,
      price_per_security_scan_brl: 74.99,
      // SEO Scans
      included_seo_scans: 0,
      price_per_seo_scan: 4.99,
      price_per_seo_scan_brl: 24.99,
      // Legacy fields
      included_metrics_events: 100000,
      included_log_events: 500000,
      included_trace_spans: 100000,
      included_storage_gb: 1,
      overage_metrics_per_1k: 0,
      overage_logs_per_1k: 0,
      overage_traces_per_1k: 0,
      overage_storage_per_gb: 0,
      features: [],
      is_public: true,
      is_active: true,
      sort_order: plans.length,
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (plan: SubscriptionPlan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      slug: plan.slug,
      description: plan.description || '',
      price_monthly: plan.price_monthly,
      price_annual: plan.price_annual,
      price_monthly_brl: (plan as any).price_monthly_brl || 0,
      price_annual_brl: (plan as any).price_annual_brl || 0,
      currency: plan.currency,
      max_applications: plan.max_applications,
      max_users: plan.max_users,
      max_hosts: plan.max_hosts,
      data_retention_days: plan.data_retention_days,
      // Pay-as-you-go pricing (USD)
      price_per_infra_host: (plan as any).price_per_infra_host || 0,
      price_per_apm_host: (plan as any).price_per_apm_host || 0,
      price_per_kubernetes_node: (plan as any).price_per_kubernetes_node || 0,
      price_per_gb_log_ingestion: (plan as any).price_per_gb_log_ingestion || 0,
      price_per_million_indexed_spans: (plan as any).price_per_million_indexed_spans || 0,
      price_per_100_custom_metrics: (plan as any).price_per_100_custom_metrics || 0,
      // Pay-as-you-go pricing (BRL)
      price_per_infra_host_brl: (plan as any).price_per_infra_host_brl || 0,
      price_per_apm_host_brl: (plan as any).price_per_apm_host_brl || 0,
      price_per_kubernetes_node_brl: (plan as any).price_per_kubernetes_node_brl || 0,
      price_per_gb_log_ingestion_brl: (plan as any).price_per_gb_log_ingestion_brl || 0,
      price_per_million_indexed_spans_brl: (plan as any).price_per_million_indexed_spans_brl || 0,
      price_per_100_custom_metrics_brl: (plan as any).price_per_100_custom_metrics_brl || 0,
      // Included allotments
      included_infra_hosts: (plan as any).included_infra_hosts || 0,
      included_apm_hosts: (plan as any).included_apm_hosts || 0,
      included_kubernetes_nodes: (plan as any).included_kubernetes_nodes || 0,
      included_gb_log_ingestion: (plan as any).included_gb_log_ingestion || 0,
      included_million_indexed_spans: (plan as any).included_million_indexed_spans || 0,
      included_custom_metrics: (plan as any).included_custom_metrics || 0,
      // RUM & Session Recordings pricing (USD)
      price_per_1000_rum_sessions: (plan as any).price_per_1000_rum_sessions || 0,
      price_per_million_rum_events: (plan as any).price_per_million_rum_events || 0,
      price_per_1000_recordings: (plan as any).price_per_1000_recordings || 0,
      price_per_gb_recording_storage: (plan as any).price_per_gb_recording_storage || 0,
      // RUM & Session Recordings pricing (BRL)
      price_per_1000_rum_sessions_brl: (plan as any).price_per_1000_rum_sessions_brl || 0,
      price_per_million_rum_events_brl: (plan as any).price_per_million_rum_events_brl || 0,
      price_per_1000_recordings_brl: (plan as any).price_per_1000_recordings_brl || 0,
      price_per_gb_recording_storage_brl: (plan as any).price_per_gb_recording_storage_brl || 0,
      // RUM & Session Recordings included allotments
      included_rum_sessions: (plan as any).included_rum_sessions || 0,
      included_rum_events: (plan as any).included_rum_events || 0,
      included_session_recordings: (plan as any).included_session_recordings || 0,
      included_recording_storage_gb: (plan as any).included_recording_storage_gb || 0,
      // Cloud Scans
      included_cloud_scans: (plan as any).included_cloud_scans || 0,
      price_per_cloud_scan: (plan as any).price_per_cloud_scan || 9.99,
      price_per_cloud_scan_brl: (plan as any).price_per_cloud_scan_brl || 49.99,
      // Uptime Monitors
      included_uptime_monitors: (plan as any).included_uptime_monitors || 0,
      price_per_uptime_monitor: (plan as any).price_per_uptime_monitor || 4.99,
      price_per_uptime_monitor_brl: (plan as any).price_per_uptime_monitor_brl || 24.99,
      // Security Scans
      included_security_scans: (plan as any).included_security_scans || 0,
      price_per_security_scan: (plan as any).price_per_security_scan || 14.99,
      price_per_security_scan_brl: (plan as any).price_per_security_scan_brl || 74.99,
      // SEO Scans
      included_seo_scans: (plan as any).included_seo_scans || 0,
      price_per_seo_scan: (plan as any).price_per_seo_scan || 4.99,
      price_per_seo_scan_brl: (plan as any).price_per_seo_scan_brl || 24.99,
      // Legacy fields
      included_metrics_events: plan.included_metrics_events,
      included_log_events: plan.included_log_events,
      included_trace_spans: plan.included_trace_spans,
      included_storage_gb: plan.included_storage_gb,
      overage_metrics_per_1k: plan.overage_metrics_per_1k || 0,
      overage_logs_per_1k: plan.overage_logs_per_1k || 0,
      overage_traces_per_1k: plan.overage_traces_per_1k || 0,
      overage_storage_per_gb: plan.overage_storage_per_gb || 0,
      features: plan.features || [],
      is_public: plan.is_public,
      is_active: plan.is_active,
      sort_order: plan.sort_order || 0,
    });
    setShowModal(true);
  };

  const handleSavePlan = async () => {
    setIsSaving(true);
    setError(null);
    try {
      if (editingPlan) {
        const response = await billingApi.updatePlan(editingPlan.id, formData);
        if (response.success) {
          toast.success(response.message || 'Plan created successfully', {
            duration: 3000,
            icon: '✅',
          });
          setShowModal(false);
          loadPlans();
        }
      } else {
        const response = await billingApi.createPlan(formData as any);
        if (response.success) {
          toast.success(response.message || 'Plan created successfully', {
            duration: 3000,
            icon: '✅',
          });
          setShowModal(false);
          loadPlans();
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to save plan');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeletePlan = async () => {
    if (!deletingPlan) return;

    setIsSaving(true);
    try {
      const response = await billingApi.deletePlan(deletingPlan.id);
      if (response.success) {
        toast.success(response.message, {
          duration: 3000,
          icon: '✅',
        });
        setShowDeleteModal(false);
        setDeletingPlan(null);
        loadPlans();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to delete plan', {
        duration: 5000,
        icon: '❌',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleSyncWithStripe = async (planId: string) => {
    try {
      const response = await billingApi.syncPlanWithStripe(planId);
      if (response.success) {
        toast.success(response.message, {
          duration: 3000,
          icon: '✅',
        });
        loadPlans();
      }
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to sync with Stripe', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleMovePlan = async (plan: SubscriptionPlan, direction: 'up' | 'down') => {
    const currentIndex = plans.findIndex((p) => p.id === plan.id);
    if (
      (direction === 'up' && currentIndex === 0) ||
      (direction === 'down' && currentIndex === plans.length - 1)
    ) {
      return;
    }

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    const newPlans = [...plans];
    [newPlans[currentIndex], newPlans[newIndex]] = [newPlans[newIndex], newPlans[currentIndex]];

    const orders = newPlans.map((p, idx) => ({ id: p.id, sort_order: idx }));

    try {
      await billingApi.reorderPlans(orders);
      setPlans(newPlans);
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Failed to reorder plans', {
        duration: 5000,
        icon: '❌',
      });
    }
  };

  const handleAddFeature = () => {
    if (featureInput.trim()) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()],
      });
      setFeatureInput('');
    }
  };

  const handleRemoveFeature = (index: number) => {
    setFormData({
      ...formData,
      features: formData.features.filter((_, i) => i !== index),
    });
  };

  const formatPrice = (price: number | string | null | undefined) => {
    if (price === null || price === undefined) return '$0.00';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '$0.00';
    return numPrice === 0 ? 'Free' : `$${numPrice.toFixed(2)}`;
  };

  const formatNumber = (num: number | string | null | undefined) => {
    if (num === null || num === undefined) return '0';
    const numValue = typeof num === 'string' ? parseFloat(num) : num;
    if (isNaN(numValue)) return '0';
    if (numValue === -1) return 'Unlimited';
    return numValue.toLocaleString();
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Subscription Plans</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Manage subscription plans and pricing
          </p>
        </div>
        <div className="flex items-center space-x-3">
          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
              className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Show Inactive
          </label>
          <button
            onClick={handleOpenCreateModal}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Plan
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 rounded-lg bg-red-50 dark:bg-red-900/20 p-4">
          <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
        </div>
      )}

      {/* Plans Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
        </div>
      ) : plans.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-gray-600 dark:text-gray-400">No plans found</p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Plan Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Price (Monthly/Annual)
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Limits
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {plans.map((plan, index) => (
                <tr key={plan.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleMovePlan(plan, 'up')}
                        disabled={index === 0}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowUpIcon className="h-4 w-4" />
                      </button>
                      <span className="text-sm text-gray-900 dark:text-white">{plan.sort_order}</span>
                      <button
                        onClick={() => handleMovePlan(plan, 'down')}
                        disabled={index === plans.length - 1}
                        className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <ArrowDownIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {plan.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{plan.slug}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">
                      {formatPrice(plan.price_monthly)} / {formatPrice(plan.price_annual)}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                      <div>Apps: {formatNumber(plan.max_applications)}</div>
                      <div>Users: {formatNumber(plan.max_users)}</div>
                      <div>Hosts: {formatNumber(plan.max_hosts)}</div>
                      <div>Retention: {plan.data_retention_days} days</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex flex-col space-y-1">
                      {plan.is_active ? (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                          <CheckCircleIcon className="h-3 w-3 mr-1" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                          <XCircleIcon className="h-3 w-3 mr-1" />
                          Inactive
                        </span>
                      )}
                      {plan.is_public && (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                          Public
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => handleSyncWithStripe(plan.id)}
                        className="p-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                        title="Sync with Stripe"
                      >
                        <ArrowPathIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleOpenEditModal(plan)}
                        className="p-1 text-indigo-600 hover:text-indigo-900 dark:text-indigo-400"
                        title="Edit"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => {
                          setDeletingPlan(plan);
                          setShowDeleteModal(true);
                        }}
                        className="p-1 text-red-600 hover:text-red-900 dark:text-red-400"
                        title="Delete"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-7xl w-full max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">
              {editingPlan ? 'Edit Plan' : 'Create New Plan'}
            </h2>

            <div className="grid grid-cols-4 gap-4">
              {/* Basic Info */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Basic Information
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Plan Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Starter"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Slug *
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase() })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="starter"
                />
              </div>

              <div className="col-span-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                  placeholder="Perfect for small teams..."
                />
              </div>

              {/* Base Pricing */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 mt-4">
                  Base Pricing
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_monthly}
                  onChange={(e) => setFormData({ ...formData, price_monthly: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Annual ($) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_annual}
                  onChange={(e) => setFormData({ ...formData, price_annual: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Monthly (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_monthly_brl}
                  onChange={(e) => setFormData({ ...formData, price_monthly_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Annual (R$) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_annual_brl}
                  onChange={(e) => setFormData({ ...formData, price_annual_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Limits */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 mt-4">
                  Limits (-1 for unlimited)
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Applications *
                </label>
                <input
                  type="number"
                  value={formData.max_applications}
                  onChange={(e) => setFormData({ ...formData, max_applications: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Users *
                </label>
                <input
                  type="number"
                  value={formData.max_users}
                  onChange={(e) => setFormData({ ...formData, max_users: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Hosts *
                </label>
                <input
                  type="number"
                  value={formData.max_hosts}
                  onChange={(e) => setFormData({ ...formData, max_hosts: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Data Retention (days) *
                </label>
                <input
                  type="number"
                  value={formData.data_retention_days}
                  onChange={(e) => setFormData({ ...formData, data_retention_days: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Pay-as-You-Go Pricing */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Pay-as-You-Go Pricing
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                  Included allotments and overage costs per additional unit
                </p>
              </div>

              {/* Infrastructure Hosts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Infra Hosts
                </label>
                <input
                  type="number"
                  value={formData.included_infra_hosts}
                  onChange={(e) => setFormData({ ...formData, included_infra_hosts: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Infra Host ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_infra_host}
                  onChange={(e) => setFormData({ ...formData, price_per_infra_host: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Infra Host (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_infra_host_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_infra_host_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* APM Hosts */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. APM Hosts
                </label>
                <input
                  type="number"
                  value={formData.included_apm_hosts}
                  onChange={(e) => setFormData({ ...formData, included_apm_hosts: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per APM Host ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_apm_host}
                  onChange={(e) => setFormData({ ...formData, price_per_apm_host: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per APM Host (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_apm_host_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_apm_host_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Kubernetes Nodes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. K8s Nodes
                </label>
                <input
                  type="number"
                  value={formData.included_kubernetes_nodes}
                  onChange={(e) => setFormData({ ...formData, included_kubernetes_nodes: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per K8s Node ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_kubernetes_node}
                  onChange={(e) => setFormData({ ...formData, price_per_kubernetes_node: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per K8s Node (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_kubernetes_node_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_kubernetes_node_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Log Ingestion */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Logs (GB)
                </label>
                <input
                  type="number"
                  value={formData.included_gb_log_ingestion}
                  onChange={(e) => setFormData({ ...formData, included_gb_log_ingestion: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per GB Log ($)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.price_per_gb_log_ingestion}
                  onChange={(e) => setFormData({ ...formData, price_per_gb_log_ingestion: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per GB Log (R$)
                </label>
                <input
                  type="number"
                  step="0.0001"
                  value={formData.price_per_gb_log_ingestion_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_gb_log_ingestion_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Indexed Spans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Spans (M)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.included_million_indexed_spans}
                  onChange={(e) => setFormData({ ...formData, included_million_indexed_spans: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per M Spans ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_million_indexed_spans}
                  onChange={(e) => setFormData({ ...formData, price_per_million_indexed_spans: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per M Spans (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_million_indexed_spans_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_million_indexed_spans_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Custom Metrics */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Metrics
                </label>
                <input
                  type="number"
                  value={formData.included_custom_metrics}
                  onChange={(e) => setFormData({ ...formData, included_custom_metrics: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 100 Metrics ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_100_custom_metrics}
                  onChange={(e) => setFormData({ ...formData, price_per_100_custom_metrics: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 100 Metrics (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_100_custom_metrics_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_100_custom_metrics_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* RUM & Session Recordings */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  RUM & Session Recordings
                </h3>
              </div>

              {/* RUM Sessions */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. RUM Sessions
                </label>
                <input
                  type="number"
                  value={formData.included_rum_sessions}
                  onChange={(e) => setFormData({ ...formData, included_rum_sessions: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 1K Sessions ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_1000_rum_sessions}
                  onChange={(e) => setFormData({ ...formData, price_per_1000_rum_sessions: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 1K Sessions (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_1000_rum_sessions_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_1000_rum_sessions_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* RUM Events */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. RUM Events (M)
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.included_rum_events / 1000000}
                  onChange={(e) => setFormData({ ...formData, included_rum_events: Math.floor((parseFloat(e.target.value) || 0) * 1000000) })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per M Events ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_million_rum_events}
                  onChange={(e) => setFormData({ ...formData, price_per_million_rum_events: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per M Events (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_million_rum_events_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_million_rum_events_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div></div>

              {/* Session Recordings */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Recordings
                </label>
                <input
                  type="number"
                  value={formData.included_session_recordings}
                  onChange={(e) => setFormData({ ...formData, included_session_recordings: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 1K Recordings ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_1000_recordings}
                  onChange={(e) => setFormData({ ...formData, price_per_1000_recordings: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per 1K Recordings (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_1000_recordings_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_1000_recordings_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Recording Storage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Storage (GB)
                </label>
                <input
                  type="number"
                  value={formData.included_recording_storage_gb}
                  onChange={(e) => setFormData({ ...formData, included_recording_storage_gb: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per GB Storage ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_gb_recording_storage}
                  onChange={(e) => setFormData({ ...formData, price_per_gb_recording_storage: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per GB Storage (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_gb_recording_storage_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_gb_recording_storage_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Cloud & Security Scans */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Cloud, Security & SEO Scans
                </h3>
              </div>

              {/* Cloud Scans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Cloud Scans
                </label>
                <input
                  type="number"
                  value={formData.included_cloud_scans}
                  onChange={(e) => setFormData({ ...formData, included_cloud_scans: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Cloud Scan ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_cloud_scan}
                  onChange={(e) => setFormData({ ...formData, price_per_cloud_scan: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Cloud Scan (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_cloud_scan_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_cloud_scan_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Uptime Monitors */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Monitors
                </label>
                <input
                  type="number"
                  value={formData.included_uptime_monitors}
                  onChange={(e) => setFormData({ ...formData, included_uptime_monitors: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Monitor ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_uptime_monitor}
                  onChange={(e) => setFormData({ ...formData, price_per_uptime_monitor: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Monitor (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_uptime_monitor_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_uptime_monitor_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Security Scans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. Security Scans
                </label>
                <input
                  type="number"
                  value={formData.included_security_scans}
                  onChange={(e) => setFormData({ ...formData, included_security_scans: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Sec. Scan ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_security_scan}
                  onChange={(e) => setFormData({ ...formData, price_per_security_scan: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per Sec. Scan (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_security_scan_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_security_scan_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* SEO Scans */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Incl. SEO Scans
                </label>
                <input
                  type="number"
                  value={formData.included_seo_scans}
                  onChange={(e) => setFormData({ ...formData, included_seo_scans: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per SEO Scan ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_seo_scan}
                  onChange={(e) => setFormData({ ...formData, price_per_seo_scan: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Per SEO Scan (R$)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.price_per_seo_scan_brl}
                  onChange={(e) => setFormData({ ...formData, price_per_seo_scan_brl: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>
              <div></div>

              {/* Legacy Fields */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Legacy Fields
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Metrics Events *
                </label>
                <input
                  type="number"
                  value={formData.included_metrics_events}
                  onChange={(e) => setFormData({ ...formData, included_metrics_events: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Log Events *
                </label>
                <input
                  type="number"
                  value={formData.included_log_events}
                  onChange={(e) => setFormData({ ...formData, included_log_events: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Trace Spans *
                </label>
                <input
                  type="number"
                  value={formData.included_trace_spans}
                  onChange={(e) => setFormData({ ...formData, included_trace_spans: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Storage (GB) *
                </label>
                <input
                  type="number"
                  value={formData.included_storage_gb}
                  onChange={(e) => setFormData({ ...formData, included_storage_gb: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Overage Costs */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Legacy Overage Costs
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Metrics (per 1k) *
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.overage_metrics_per_1k}
                  onChange={(e) => setFormData({ ...formData, overage_metrics_per_1k: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Logs (per 1k) *
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.overage_logs_per_1k}
                  onChange={(e) => setFormData({ ...formData, overage_logs_per_1k: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Traces (per 1k) *
                </label>
                <input
                  type="number"
                  step="0.001"
                  value={formData.overage_traces_per_1k}
                  onChange={(e) => setFormData({ ...formData, overage_traces_per_1k: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Storage (per GB) *
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.overage_storage_per_gb}
                  onChange={(e) => setFormData({ ...formData, overage_storage_per_gb: parseFloat(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              {/* Features */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Features
                </h3>
                <div className="flex items-center space-x-2 mb-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddFeature()}
                    className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                    placeholder="Add a feature..."
                  />
                  <button
                    onClick={handleAddFeature}
                    className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature, index) => (
                    <div
                      key={index}
                      className="flex items-center bg-gray-50 dark:bg-gray-700 px-3 py-1 rounded-md"
                    >
                      <span className="text-sm text-gray-900 dark:text-white mr-2">{feature}</span>
                      <button
                        onClick={() => handleRemoveFeature(index)}
                        className="text-red-600 hover:text-red-800"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Settings */}
              <div className="col-span-4">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2 mt-4">
                  Settings
                </h3>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Sort Order
                </label>
                <input
                  type="number"
                  value={formData.sort_order}
                  onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white"
                />
              </div>

              <div className="flex items-center space-x-6 col-span-3">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_public}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Public</span>
                </label>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                    className="mr-2 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
                </label>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSavePlan}
                disabled={isSaving}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50"
              >
                {isSaving ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingPlan && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              Delete Plan
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Are you sure you want to delete the "{deletingPlan.name}" plan? This will set it as
              inactive and it will no longer be available for new subscriptions.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeletingPlan(null);
                }}
                className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleDeletePlan}
                disabled={isSaving}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {isSaving ? 'Deleting...' : 'Delete Plan'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  </MainLayout>
  );
}
